import { useState } from 'react';
import type { CatProduct } from '@/mocks/categoryProducts';

interface RemindMeModalProps {
  product: CatProduct;
  onClose: () => void;
}

const REMINDERS_KEY = 'clothybazar_reminders';

interface Reminder {
  productId: number;
  productName: string;
  phone: string;
  date: string;
}

export function getReminders(): Reminder[] {
  try {
    const raw = localStorage.getItem(REMINDERS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

export function isReminded(productId: number): boolean {
  return getReminders().some(r => r.productId === productId);
}

function saveReminder(productId: number, productName: string, phone: string) {
  const reminders = getReminders().filter(r => r.productId !== productId);
  reminders.push({ productId, productName, phone, date: new Date().toISOString() });
  localStorage.setItem(REMINDERS_KEY, JSON.stringify(reminders));
}

export default function RemindMeModal({ product, onClose }: RemindMeModalProps) {
  const [phone, setPhone] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const alreadySet = isReminded(product.id);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const cleaned = phone.replace(/\s/g, '');
    if (!/^[6-9]\d{9}$/.test(cleaned)) {
      setError('Please enter a valid 10-digit Indian mobile number');
      return;
    }
    saveReminder(product.id, product.name, cleaned);
    setSuccess(true);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden animate-slide-up">

        {/* Header strip */}
        <div className="bg-gradient-to-r from-orange-500 to-red-500 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
              <i className="ri-notification-3-fill text-white"></i>
            </div>
            <p className="text-white font-bold text-base">Notify Me</p>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-white text-sm"></i>
          </button>
        </div>

        <div className="p-5">
          {/* Product preview */}
          <div className="flex items-center gap-3 mb-5 p-3 bg-orange-50 rounded-xl border border-orange-100">
            <div className="relative shrink-0">
              <img
                src={product.image}
                alt={product.name}
                className="w-14 h-16 object-cover object-top rounded-lg"
              />
              <div className="absolute inset-0 rounded-lg bg-black/30 flex items-center justify-center">
                <span className="text-white text-[9px] font-bold text-center leading-tight px-1">OUT OF{'\n'}STOCK</span>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-gray-900 font-semibold text-sm leading-tight truncate">{product.name}</p>
              <div className="flex items-center gap-1 mt-1">
                <span className="text-gray-900 font-bold text-base">₹{product.price}</span>
                <span className="text-gray-400 text-xs line-through">₹{product.originalPrice}</span>
              </div>
              <span className="inline-flex items-center gap-1 mt-1 bg-red-100 text-red-600 text-[10px] font-bold px-2 py-0.5 rounded-full">
                <i className="ri-close-circle-fill"></i> Out of Stock
              </span>
            </div>
          </div>

          {success || alreadySet ? (
            /* Success State */
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                <i className="ri-notification-badge-fill text-green-500 text-3xl"></i>
              </div>
              <p className="text-gray-900 font-bold text-lg mb-1">
                {alreadySet && !success ? 'Already Registered!' : 'Reminder Set!'}
              </p>
              <p className="text-gray-500 text-sm leading-relaxed">
                {alreadySet && !success
                  ? `You'll be notified on WhatsApp when "${product.name}" is back in stock.`
                  : `We'll send you a WhatsApp notification when "${product.name}" is back in stock!`
                }
              </p>
              <div className="mt-4 flex items-center justify-center gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-2.5">
                <i className="ri-whatsapp-fill text-green-500 text-lg"></i>
                <span className="text-green-700 text-sm font-semibold">WhatsApp notification set</span>
              </div>
              <button
                onClick={onClose}
                className="mt-4 w-full py-3 rounded-xl bg-gray-100 text-gray-700 font-semibold text-sm hover:bg-gray-200 transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          ) : (
            /* Input Form */
            <>
              <p className="text-gray-600 text-sm mb-4 text-center">
                Enter your WhatsApp number — we'll ping you the moment it's back! 🔔
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                    WhatsApp Number
                  </label>
                  <div className="flex items-center gap-2 px-4 py-3 rounded-xl border-2 border-gray-200 focus-within:border-orange-500 focus-within:ring-2 focus-within:ring-orange-500/20 transition-all bg-white">
                    <div className="flex items-center gap-1.5 shrink-0 border-r border-gray-200 pr-3">
                      <span className="text-base">🇮🇳</span>
                      <span className="text-gray-700 text-sm font-semibold">+91</span>
                    </div>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => { setPhone(e.target.value); setError(''); }}
                      placeholder="98765 43210"
                      maxLength={10}
                      className="flex-1 outline-none text-gray-900 text-sm placeholder-gray-400 bg-transparent"
                      autoFocus
                    />
                    {phone.length === 10 && (
                      <i className="ri-check-line text-green-500 shrink-0"></i>
                    )}
                  </div>
                  {error && (
                    <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                      <i className="ri-error-warning-line"></i>{error}
                    </p>
                  )}
                </div>

                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
                  <i className="ri-whatsapp-fill text-green-500 text-lg shrink-0 mt-0.5"></i>
                  <p className="text-amber-800 text-xs leading-relaxed">
                    You'll receive a WhatsApp message as soon as this item is restocked. No spam, only one notification!
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={phone.length < 10}
                  className="w-full py-3.5 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold text-sm flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-orange-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-notification-3-fill"></i>
                  Notify Me When Available
                </button>

                <button
                  type="button"
                  onClick={onClose}
                  className="w-full py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
